import React, { useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import {
    useTheme, 
    useMediaQuery,
    Box, 
    Button, 
    TextField, 
    IconButton, 
    Tooltip,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { de, enGB } from 'date-fns/locale';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { 
    Add,
    Edit,
    Delete,
    Save,
    Cancel 
} from "@mui/icons-material";
import CustomDatePicker from "components/CustomDatePicker";


const DependentsDialog = ({ 
    open, 
    onClose, 
    selectedCrew = {}, 
    onSave 
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const [rows, setRows] = useState(selectedCrew?.dependents || []);
    const [editingIndex, setEditingIndex] = useState(null);

    const [form, setForm] = useState({
        dependentName: "",
        relationship: "",
        birthDate: null,
        nationalIdNumber: "",
        validSince: null,
        validUntil: null,
    });

    const handleAdd = () => {
        setEditingIndex(rows.length);
        setForm({
            dependentName: "",
            relationship: "",
            birthDate: null,
            nationalIdNumber: "",
            validSince: null,
            validUntil: null,
        });
    };
    
    const handleEdit = useCallback((index) => {
        const selected = rows[index];
        setForm({
            ...selected,
            birthDate: selected.birthDate ? new Date(selected.birthDate) : null,
            validSince: selected.validSince ? new Date(selected.validSince) : null,
            validUntil: selected.validUntil ? new Date(selected.validUntil) : null,
        });

        setEditingIndex(index);
    }, [rows]);

    const handleDelete = useCallback((index) => {
        const updated = rows.filter((_, i) => i !== index);
        setRows(updated);
    }, [rows]);

    const handleAddToList = () => {
        const updated = [...rows];
        if (editingIndex === rows.length) updated.push(form);
        else updated[editingIndex] = form;
        setRows(updated);
        setEditingIndex(null);
    };

    const handleDateChange = (field, newValue) => {
        setForm((prev) => ({ ...prev, [field]: newValue }));
    };
    
    const columns = useMemo(() => [
        { field: "dependentName", headerName: "FullName", flex: 2 },
        { field: "relationship", headerName: "Relationship", flex: 1 },
        {
            field: "birthDate",
            headerName: "BirthDate",
            flex: 1,
            valueFormatter: (value) => {
                if (value == null) {
                    return '';
                }
                return `${format(value, "dd/MM/yyyy")}`;
            },
        },
        { field: "nationalIdNumber", headerName: "NationalIDNumber", flex: 1 },
        {
            field: "validSince",
            headerName: "ValidSince",
            flex: 1,
            valueFormatter: (value) => {
                if (value == null) {
                    return '';
                }
                return `${format(value, "dd/MM/yyyy")}`;
            },
        },
        {
            field: "validUntil",
            headerName: "ValidUntil",
            flex: 1,
            valueFormatter: (value) => {
                if (value == null) {
                    return '';
                }
                return `${format(value, "dd/MM/yyyy")}`;
            },
        },        
        {
            field: "action",
            headerName: "Action",
            flex: 0.8,
            sortable: false,
            renderCell: (params) => {
                const index = params.api.getAllRowIds().indexOf(params.id);
                return (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                            <IconButton onClick={() => handleEdit(index)}>
                                <Edit />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton 
                                color="error" 
                                onClick={() => handleDelete(index)}
                            >
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ], [handleEdit, handleDelete]);

    return (
        <LocalizationProvider 
            dateAdapter={AdapterDateFns}
            adapterLocale={locales['en-gb']}
        >
            <Dialog 
                fullWidth 
                maxWidth="lg" 
                open={open} 
                onClose={onClose}
            >
                <DialogTitle fontWeight="bold" color="primary">
                    {selectedCrew.crewCode} - {selectedCrew.crewName}
                </DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <Button
                            startIcon={<Add />}
                            variant="outlined"
                            onClick={handleAdd}
                        >
                            Add Dependent
                        </Button>
                    </Box>

                    <Box height='40vh'>
                        <DataGrid
                            rows={rows.map((r, i) => ({ id: i, ...r }))}
                            columns={columns}
                            disableRowSelectionOnClick
                        />
                    </Box>

                    {editingIndex !== null && (
                        <Box
                            display="grid"
                            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                            gap={2}
                            mt={2}
                        >
                            {/* Row 1 */}
                            <TextField
                                label="Name"
                                value={form.dependentName}
                                onChange={(e) => setForm({ ...form, dependentName: e.target.value })}
                                sx={{ gridColumn: "span 4" }}
                            />

                            <TextField
                                label="Relationship"
                                value={form.relationship}
                                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                                sx={{ gridColumn: "span 4" }}
                            />

                            <CustomDatePicker
                                label="Birth Date"
                                name="birthDate"
                                value={form.birthDate}
                                maxDate={new Date()}
                                onChange={handleDateChange}
                                sx={{ gridColumn: "span 4" }}
                            />

                            {/* Row 2 */}
                            <TextField
                                label="National ID"
                                value={form.nationalIdNumber}
                                onChange={(e) =>
                                    setForm({ ...form, nationalIdNumber: e.target.value })
                                }
                                sx={{ gridColumn: "span 4" }}
                            />

                            <CustomDatePicker
                                label="Valid Since"
                                name="validSince"
                                value={form.validSince}
                                maxDate={form.validUntil}
                                onChange={handleDateChange}
                                sx={{ gridColumn: "span 4" }}
                            />

                            <CustomDatePicker
                                label="Valid Until"
                                name="validUntil"
                                value={form.validUntil}
                                minDate={form.validSince}
                                onChange={handleDateChange}
                                sx={{ gridColumn: "span 4" }}
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    {editingIndex !== null && (
                        <Button
                            variant="outlined"
                            color="success"
                            startIcon={<Add />}
                            onClick={handleAddToList}
                        >
                            {!isMobile ? "ADD TO LIST" : "ADD"}
                        </Button>
                    )}
                    {editingIndex !== null && (
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<Cancel />}
                            onClick={() => setEditingIndex(null)}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button 
                        variant="outlined"
                        color="primary"
                        startIcon={<Save />}
                        onClick={() => onSave(rows)} 
                    >
                        Save
                    </Button>
                    <Button 
                        variant="outlined"
                        color="secondary"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
        
    );
};

export default DependentsDialog;
